/**
 * Validation Rules Section — settings section for managing CRM validation rules.
 *
 * Mirrors the Custom Properties UI (Objects & Fields): a section heading, a
 * primary "Create" button, HorizontalTabs to switch between target entity
 * types, and a bordered Table per tab listing that type's rules (or an empty
 * state). Rules are agent-authored — clicking a rule name opens the standard
 * CRM object slideout; the inline action is the enable/disable toggle.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, ShieldTick } from '@untitledui/icons';
import { memo, type ReactNode, useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { useAuthInfo } from '@/allauth_auth/hooks.js';
import { useOrg } from '@/contexts/OrgContext';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { usePermissions } from '@/hooks/usePermissions';
import { Badge } from '@/lib/base/badges/badges';
import { Button } from '@/lib/base/buttons/button';
import { EmptyState } from '@/lib/base/empty-state';
import { Toggle } from '@/lib/base/forms/toggle';
import { FeaturedIcon } from '@/lib/base/foundations/featured-icon/featured-icon';
import { CARD_BORDER } from '@/lib/base/list-card';
import { Table } from '@/lib/base/table';
import HorizontalTabs from '@/lib/base/tabs/HorizontalTabs';
import { Tooltip } from '@/lib/base/tooltip/tooltip';
import { QueryErrorState } from '@/lib/components/QueryErrorState';
import { buildCreateInBuilderNavState } from '@/lib/crm/components/badges/buildBuilderModeNavState';
import {
  getUserFriendlyEventError,
  PropertyTypeError,
} from '@/lib/crm/core/errors';
import { createEventBuilder } from '@/lib/crm/core/EventBuilder';
import { createEventsClient } from '@/lib/crm/core/EventsClient';
import { createSchemaClient } from '@/lib/crm/core/SchemaClient';
import { useSlideoutStack } from '@/lib/crm/hooks/useSlideoutStack';
import {
  crmObjectListSentinelQuery,
  useStoreBackedList,
} from '@/lib/crm/store';
import { logger } from '@/lib/logger';
import type { CrmObject } from '@/types/crm';
import { cx } from '@/utils/cx';

const CREATE_DISABLED_TOOLTIP =
  'Only admins have permission to create validation rules';

// Seeds the builder composer. Plain text, not an `<Entity>` mention token —
// there is no rule to reference yet.
const CREATE_RULE_PROMPT = 'I want to create a CRM validation rule';

// Entity kinds a validation rule may target — mirrors the `entity_kinds`
// field_type_config on ahoy_target_entity_type (Deal=case, Contact/Company=
// party, custom assets=asset). Used to build the tab set.
const VALIDATION_TARGET_KINDS = ['case', 'party', 'asset'] as const;

// ============================================================================
// ValidationRuleRow
// ============================================================================

interface ValidationRuleRowProps {
  rule: CrmObject;
  onToggle: (entityId: string, enabled: string) => void;
  onRowClick: (entityId: string) => void;
  isToggling?: boolean;
  canToggle?: boolean;
}

const ValidationRuleRow = memo(function ValidationRuleRow({
  rule,
  onToggle,
  onRowClick,
  isToggling,
  canToggle = false,
}: ValidationRuleRowProps) {
  const name =
    (rule.properties?.['ahoy_display_name']?.value as string | undefined) ??
    'Untitled rule';
  const enabled =
    (rule.properties?.['ahoy_enabled']?.value as string | undefined) === 'true';

  // Health is system-managed and only ever flips to `errored` when a formula
  // fails at evaluation; anything else (including unset) is healthy. The Status
  // column shows a Healthy or Error badge accordingly.
  const isErrored =
    (rule.properties?.['ahoy_health']?.value as string | undefined) ===
    'errored';

  const handleOpen = useCallback(() => {
    onRowClick(rule.entity_id);
  }, [rule.entity_id, onRowClick]);

  const handleToggleClick = useCallback(
    (isSelected: boolean) => {
      onToggle(rule.entity_id, isSelected ? 'true' : 'false');
    },
    [rule.entity_id, onToggle]
  );

  const toggle = canToggle ? (
    <Toggle
      isSelected={enabled}
      isDisabled={isToggling}
      aria-label={
        enabled ? 'Disable validation rule' : 'Enable validation rule'
      }
      onChange={handleToggleClick}
    />
  ) : (
    // `interactive` switches Tooltip to a non-button wrapper, so the disabled
    // Toggle (a switch/button) isn't nested inside another button — react-aria
    // would otherwise emit invalid nested interactive controls.
    <Tooltip
      title='Only admins can edit validation rules'
      placement='top'
      interactive={true}
    >
      <Toggle
        isSelected={enabled}
        isDisabled
        aria-label={
          enabled ? 'Disable validation rule' : 'Enable validation rule'
        }
        onChange={handleToggleClick}
      />
    </Tooltip>
  );

  return (
    <Table.Row id={rule.entity_id}>
      <Table.Cell>
        <button
          type='button'
          onClick={handleOpen}
          aria-label={`Open ${name} details`}
          className='text-sm font-medium text-primary text-left hover:underline cursor-pointer'
        >
          {name}
        </button>
      </Table.Cell>
      <Table.Cell>
        {isErrored ? (
          <span title='This rule failed to evaluate and is not being enforced'>
            <Badge color='error'>Error</Badge>
          </span>
        ) : (
          <Badge color='success'>Healthy</Badge>
        )}
      </Table.Cell>
      <Table.Cell>
        <div className='flex items-center justify-end'>{toggle}</div>
      </Table.Cell>
    </Table.Row>
  );
});

// ============================================================================
// ValidationRulesContent
// ============================================================================

interface ValidationRulesContentProps {
  canCreate: boolean;
  onCreate: () => void;
}

function ValidationRulesContent({
  canCreate,
  onCreate,
}: ValidationRulesContentProps) {
  const { currentOrg } = useOrg();
  const orgId = currentOrg?.id;
  const { user } = useAuthInfo();
  const queryClient = useQueryClient();
  const { openSlideout } = useSlideoutStack();
  const { hasPermission } = usePermissions();
  const canToggle = hasPermission(
    'ahoy_validation_rule',
    'ahoy_property_updated'
  );

  // Tabs: every entity type eligible as a validation target (case/party/asset
  // kinds), mirroring the Custom Properties tab set.
  const { data: targetTabs = [], isLoading: isLoadingTabs } = useQuery({
    queryKey: ['objectTypes', orgId, 'validationRuleTargets'],
    queryFn: async () => {
      if (!orgId) {
        return [] as { id: string; label: string }[];
      }
      const client = createSchemaClient(orgId);
      const objectTypes = await client.getObjectTypes();
      return objectTypes
        .filter(t =>
          VALIDATION_TARGET_KINDS.some(kind => t.entity_kind.includes(kind))
        )
        .map(t => ({
          id: t.entity_type_name,
          label: t.label_plural || t.label,
        }));
    },
    enabled: !!orgId,
  });

  const [selectedType, setSelectedType] = useState<string | null>(null);
  const activeTab = useMemo(() => {
    if (selectedType && targetTabs.some(t => t.id === selectedType)) {
      return selectedType;
    }
    return targetTabs.length > 0 ? targetTabs[0].id : null;
  }, [selectedType, targetTabs]);
  const handleTabChange = useCallback((tabId: string) => {
    setSelectedType(tabId);
  }, []);

  const query = crmObjectListSentinelQuery(orgId ?? 0, 'ahoy_validation_rule');
  const listQuery = useQuery({ ...query, enabled: !!orgId });
  const { results, isLoading, error, refetch } = useStoreBackedList(listQuery);

  const toggleMutation = useMutation({
    mutationFn: async ({
      entityId,
      enabled,
    }: {
      entityId: string;
      enabled: string;
    }) => {
      if (!orgId || !user) {
        throw new Error('Organization not available');
      }
      const rule = results.find(r => r.entity_id === entityId);
      const builder = createEventBuilder({
        sourceId: String(user.id),
        startSeq: rule?.last_event_seq ?? 0,
      });
      builder.propertyUpdated(
        entityId,
        'ahoy_validation_rule',
        'ahoy_enabled',
        enabled
      );
      await createEventsClient(orgId).submitEvents(builder.build());
    },
    onError: err => {
      logger.error(
        'Failed to update validation rule',
        err instanceof Error ? err : new Error(String(err)),
        {
          component: 'crm_events',
          org_id: orgId,
          error_type: err instanceof PropertyTypeError ? err.code : undefined,
          raw_message: String(err),
        }
      );
      toast.error(getUserFriendlyEventError(err));
      queryClient.invalidateQueries({ queryKey: query.queryKey, exact: false });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: query.queryKey, exact: false });
      toast.success('Validation rule updated');
    },
  });

  const handleToggle = useCallback(
    (entityId: string, enabled: string) => {
      toggleMutation.mutate({ entityId, enabled });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- .mutate is stable per React Query guarantees
    [toggleMutation.mutate]
  );

  const handleRowClick = useCallback(
    (entityId: string) => {
      openSlideout(entityId);
    },
    [openSlideout]
  );

  // Rules whose target entity type matches the active tab.
  const rulesForTab = useMemo(() => {
    if (!activeTab) {
      return [];
    }
    return results.filter(
      rule =>
        String(rule.properties?.['ahoy_target_entity_type']?.value ?? '') ===
        activeTab
    );
  }, [results, activeTab]);

  // The rules query 400s/404s until the validation-rule entity type is
  // provisioned for the org — treat that as "no rules yet" rather than an error.
  const statusCode =
    error !== null &&
    typeof error === 'object' &&
    'statusCode' in error &&
    typeof (error as Record<string, unknown>).statusCode === 'number'
      ? (error as { statusCode: number }).statusCode
      : undefined;
  const isNotProvisioned = statusCode === 400 || statusCode === 404;

  if (error && !isNotProvisioned) {
    return (
      <QueryErrorState
        error={error}
        label="We couldn't load your validation rules"
        onRetry={refetch}
      />
    );
  }

  const isBusy = isLoading || isLoadingTabs;

  let cardBody: ReactNode;
  if (isBusy) {
    cardBody = (
      <div className='space-y-3'>
        {[1, 2].map(i => (
          <div key={i} className='h-12 bg-bg-tertiary rounded animate-pulse' />
        ))}
      </div>
    );
  } else if (rulesForTab.length === 0) {
    cardBody = (
      <div className={cx(CARD_BORDER, 'bg-bg-secondary')}>
        <EmptyState
          icon={
            <FeaturedIcon
              icon={ShieldTick}
              size='lg'
              theme='light-outline'
              color='gray'
            />
          }
          title='No validation rules for this type'
          description={
            canCreate
              ? 'Create your first validation rule to enforce data quality on these records.'
              : 'No validation rules have been created for this type yet.'
          }
          action={{
            label: 'Create validation rule',
            onClick: onCreate,
            icon: Plus,
            color: 'primary',
            isDisabled: !canCreate,
            disabledTooltip: CREATE_DISABLED_TOOLTIP,
          }}
        />
      </div>
    );
  } else {
    cardBody = (
      <div className='overflow-hidden rounded-lg border border-border-secondary'>
        <Table aria-label='Validation rules' size='sm'>
          <Table.Header>
            <Table.Head label='Name' isRowHeader />
            <Table.Head label='Status' />
            <Table.Head label='Active' align='right' />
          </Table.Header>
          <Table.Body>
            {rulesForTab.map(rule => (
              <ValidationRuleRow
                key={rule.entity_id}
                rule={rule}
                onToggle={handleToggle}
                onRowClick={handleRowClick}
                isToggling={toggleMutation.isPending}
                canToggle={canToggle}
              />
            ))}
          </Table.Body>
        </Table>
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      {isBusy ? (
        <div className='flex gap-2' aria-label='Loading validation rules'>
          {[1, 2, 3].map(i => (
            <div
              key={i}
              className='h-8 w-24 bg-bg-tertiary rounded animate-pulse'
            />
          ))}
        </div>
      ) : (
        <HorizontalTabs
          tabs={[...targetTabs]}
          activeTab={activeTab ?? ''}
          onTabChange={handleTabChange}
          idPrefix='validation-target'
        />
      )}

      <div className='mt-6'>{cardBody}</div>
    </div>
  );
}

// ============================================================================
// ValidationRulesSection
// ============================================================================

export function ValidationRulesSection() {
  const { currentOrg } = useOrg();
  const orgId = currentOrg?.id;
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const canCreate = hasPermission(
    'ahoy_validation_rule',
    'ahoy_object_created'
  );
  // Gates only the DESTINATION of the create CTA, not the CTA itself — with
  // the flag off the button still works, it just lands where it used to.
  const isBuilderModeEnabled = useFeatureFlag('builder_mode');

  const handleCreate = useCallback(() => {
    if (!orgId) {
      return;
    }
    if (isBuilderModeEnabled) {
      const { to, state } = buildCreateInBuilderNavState(
        orgId,
        'ahoy_validation_rule',
        CREATE_RULE_PROMPT
      );
      navigate(to, { state });
      return;
    }
    // Flag off — the pre-builder destination: the conversations home screen
    // with the composer seeded. Kept so turning the flag off is a true revert
    // rather than a dead button.
    navigate(`/${orgId}/conversations`, {
      state: {
        initialPrompt: CREATE_RULE_PROMPT,
        fromConversationsHome: true,
      },
    });
  }, [orgId, navigate, isBuilderModeEnabled]);

  return (
    <div className='space-y-8'>
      <div>
        <h2 className='text-lg font-semibold text-fg-primary'>
          Validation rules
        </h2>
        <p className='text-sm text-fg-tertiary mt-0.5'>
          Rules that enforce data quality on CRM records.
        </p>
      </div>

      <div>
        <div className='flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4'>
          <h3 className='text-sm font-medium text-fg-primary'>Rules</h3>
          <Button
            size='sm'
            color='primary'
            iconLeading={Plus}
            onClick={handleCreate}
            isDisabled={!orgId || !canCreate}
            disabledTooltip={CREATE_DISABLED_TOOLTIP}
            className='self-end sm:self-auto'
          >
            Create validation rule
          </Button>
        </div>
        <ValidationRulesContent canCreate={canCreate} onCreate={handleCreate} />
      </div>
    </div>
  );
}
